import { HttpEndpoint } from '@/api/httpEndpoints';
import {
    AirportSuggestion,
    CategorizedSuggestion,
    ContentEndPointModel,
    ContentGetHotelsResponse,
} from '@/api/Content/content.interfaces';
import { Market } from '@shared/config/market/market.models';
import { getCurrentMarketSpecs } from '@shared/config/market/market.helpers';

const PATH_ENDPOINT = 'https://content-hc.sembo.com';
const PATH_AUTO_SUGGESTION = 'autosuggestion';
const PATH_CATEGORIZED_AUTO_SUGGESTION = 'categorizedautosuggestion';
const PATH_GET_HOTELS = 'hotels/gethotels';
const PATH_GET_HOTEL_CODES = 'hotels/gethotelcodes';

const TYPE = 'Airport';
const PAGE = '0';
const SIZE = '1';
const MAX_NUMBER_OF_HOTELS = '5';

const market: Market = getCurrentMarketSpecs();

const getHost = (market: Market) => {
    const endSchema = '://';
    const pathIndex = market.homePage.indexOf(endSchema) + endSchema.length;
    return market.homePage.slice(pathIndex);
};

const headers = { 'X-Sembo-Host': getHost(market), 'X-Sembo-App': 'Shop' };

export class ContentEndpoint extends HttpEndpoint implements ContentEndPointModel {
    public constructor(applicationUrl: string) {
        super(applicationUrl);
    }

    public async GetAirportsByPolygonId(
        polygonId: number,
        size: number,
        skipAirportGroups: boolean
    ): Promise<AirportSuggestion[] | undefined> {
        return await getAirportsByPolygonIdWithFetch(polygonId, size, skipAirportGroups);
    }

    public async GetAirportByHotelCode(
        hotelCode: string
    ): Promise<AirportSuggestion | undefined> {
        const airports = await getAirportByHotelCodeWithFetch(hotelCode);
        return airports[0];
    }

    public async GetAirportByLocation(
        location: string | null | undefined
    ): Promise<AirportSuggestion | undefined> {
        if (location) {
            const airports = await getAirportByLocationNameWithFetch(location);
            return airports[0];
        }
        return undefined;
    }

    public async GetSuggestionByTerm(
        term: string
    ): Promise<CategorizedSuggestion | undefined> {
        const suggestions = await getCategorizedSuggestionsByFetch(term);
        return suggestions[0];
    }

    public async GetAirportSuggestions(iata: string): Promise<AirportSuggestion[]> {
        return getAirPortSuggestionsByFetch(iata);
    }

    public async GetHotelCodes(destination: string): Promise<string[]> {
        return getHotelCodesByFetch(destination);
    }

    public async GetHotels(search: string): Promise<ContentGetHotelsResponse> {
        return getHotelsByFetch(search);
    }

    public async GetCategorizedSuggestions(term: string): Promise<CategorizedSuggestion[]> {
        return getCategorizedSuggestionsByFetch(term);
    }
}

export class FakeContentEndpoint extends HttpEndpoint implements ContentEndPointModel {
    public async GetAirportsByPolygonId(
        polygonId: number,
        size: number,
        skipAirportGroups: boolean
    ): Promise<AirportSuggestion[] | undefined> {
        return await getAirportsByPolygonIdWithFetch(polygonId, size, skipAirportGroups);
    }

    public async GetAirportByHotelCode(
        hotelCode: string
    ): Promise<AirportSuggestion | undefined> {
        const airports = await getAirportByHotelCodeWithFetch(hotelCode);
        return airports[0];
    }

    public async GetAirportByLocation(
        location: string | null | undefined
    ): Promise<AirportSuggestion | undefined> {
        if (location) {
            const airports = await getAirportByLocationNameWithFetch(location);
            return airports[0];
        }
        return undefined;
    }

    public async GetSuggestionByTerm(
        term: string
    ): Promise<CategorizedSuggestion | undefined> {
        const suggestions = await getCategorizedSuggestionsByFetch(term);
        return suggestions[0];
    }

    public async GetAirportSuggestions(iata: string): Promise<AirportSuggestion[]> {
        return getAirPortSuggestionsByFetch(iata);
    }

    public async GetHotelCodes(destination: string): Promise<string[]> {
        return getHotelCodesByFetch(destination);
    }

    public async GetHotels(search: string): Promise<ContentGetHotelsResponse> {
        return getHotelsByFetch(search);
    }

    public async GetCategorizedSuggestions(term: string): Promise<CategorizedSuggestion[]> {
        return getCategorizedSuggestionsByFetch(term);
    }
}

async function getHotelsByFetch(search: string): Promise<ContentGetHotelsResponse> {
    const includes = [
        'Hotels.Name',
        'Hotels.IsCarefullySelected',
        'Hotels.IsPerfectMatch',
        'Hotels.HotelNameSlug',
        'Hotels.HotelCode',
        'Hotels.Description.Value',
        'Hotels.Address.City',
        'Hotels.Address.Country',
        'Hotels.Images.ImageService',
        'Hotels.Images.ImageName',
        'Hotels.SemboSunRating',
        'Hotels.TypeOfHotel',
    ];

    const url =
        [PATH_ENDPOINT, PATH_GET_HOTELS].join('/') +
        `?SearchText=${search}` +
        includes.map((x) => `&Include=${x}`).join(',');

    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    return await response.json();
}

async function getAirPortSuggestionsByFetch(iata: string): Promise<AirportSuggestion[]> {
    const url =
        [PATH_ENDPOINT, PATH_AUTO_SUGGESTION].join('/') +
        `?IataCode=${iata}` +
        `&Types=${TYPE}` +
        `&Page=${PAGE}` +
        `&Size=${SIZE}`;

    const response = await fetch(url, { method: 'GET', headers });
    return await response.json();
}

async function getCategorizedSuggestionsByFetch(
    term: string
): Promise<CategorizedSuggestion[]> {
    const url = [PATH_ENDPOINT, PATH_CATEGORIZED_AUTO_SUGGESTION].join('/') + `?Term=${term}`;

    const response = await fetch(url, { method: 'GET', headers });
    return await response.json();
}

async function getHotelCodesByFetch(destination: string): Promise<string[]> {
    const url =
        `${[PATH_ENDPOINT, PATH_GET_HOTEL_CODES].join('/')}` +
        `?SearchText=${destination}` +
        `&MaxNumberOfHotels=${MAX_NUMBER_OF_HOTELS}`;

    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    return await response.json();
}

async function getAirportsByPolygonIdWithFetch(
    polygonId: number,
    size: number,
    skipAirportGroups: boolean
): Promise<AirportSuggestion[]> {
    const url =
        [PATH_ENDPOINT, PATH_AUTO_SUGGESTION].join('/') +
        `?Term=polygon-id:${polygonId}` +
        `&Types=AirportForDestination` +
        `&Page=0` +
        `&Size=${size}` +
        `&SkipAirportGroups=${skipAirportGroups}`;

    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    return await response.json();
}

async function getAirportByHotelCodeWithFetch(
    hotelCode: string
): Promise<AirportSuggestion[]> {
    const url =
        [PATH_ENDPOINT, PATH_AUTO_SUGGESTION].join('/') +
        `?Term=hotel-code:${hotelCode}` +
        `&Types=AirportForDestination` +
        `&Page=${PAGE}` +
        `&Size=${SIZE}`;

    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    return await response.json();
}

async function getAirportByLocationNameWithFetch(
    location: string
): Promise<AirportSuggestion[]> {
    const url =
        [PATH_ENDPOINT, PATH_AUTO_SUGGESTION].join('/') +
        `?Term=${location}` +
        `&Types=AirportForDestination` +
        `&Page=${PAGE}` +
        `&Size=${SIZE}`;

    const response = await fetch(url, {
        method: 'GET',
        headers,
    });

    return await response.json();
}
